import { flatMap, sortBy } from 'lodash';
import { defineStore } from 'pinia';
import { ref } from 'vue';

import { crossbeamApi } from '@/api';
import {
  CROSSBEAM_DEFAULT_LABEL,
  CROSSBEAM_DEFAULT_PRESET_TYPE,
} from '@/constants/data_shares';
import { TEMP_TEAM_DEFAULT_PRESET } from '@/constants/feature_flags';
import { initStore } from '@/stores/store-utils';
import {
  DataSharePreset,
  PresetOptionItem,
  PresetOptionsGroup,
} from '@/types/data_share_presets';

import { useFeatureFlagStore } from './FeatureFlagStore';

export const useDataSharePresetsStore = defineStore(
  'DataSharePresetsStore',
  () => {
    const allPresets = ref<DataSharePreset[]>([]);
    const { hasFeatureFlag } = useFeatureFlagStore();

    const { ready, readySync, refresh } = initStore(async () => {
      const { data, error } = await crossbeamApi.GET(
        '/v0.1/data-share-presets',
      );
      if (error) throw error;
      if (data) allPresets.value = data;
    });

    function getFilteredPresetsByFeeId(feedId: number, baseSourceId?: number) {
      // CRM presets
      const presetsByFeedId =
        allPresets.value?.filter((item) => item.feed_id === feedId) || [];

      // Gsheet or Csv presets
      const presetsByBaseSourceId =
        presetsByFeedId.filter(
          (item) => item.base_source_id === baseSourceId,
        ) || [];

      return baseSourceId ? presetsByBaseSourceId : presetsByFeedId;
    }

    function isCrossbeamPreset(preset: DataSharePreset) {
      return preset.preset_type === CROSSBEAM_DEFAULT_PRESET_TYPE;
    }

    function isTeamDefault(preset: DataSharePreset) {
      return hasFeatureFlag(TEMP_TEAM_DEFAULT_PRESET)
        ? preset.is_default
        : isCrossbeamPreset(preset);
    }

    function getDefaultPresetId(feedId: number, baseSourceId?: number) {
      return getFilteredPresetsByFeeId(feedId, baseSourceId).find(isTeamDefault)
        ?.id;
    }

    function getCrossbeamPresetId(feedId: number, baseSourceId?: number) {
      return getFilteredPresetsByFeeId(feedId, baseSourceId).find(
        isCrossbeamPreset,
      )?.id;
    }

    function getPresetName(
      feedId: number,
      baseSourceId?: number,
      presetId?: number,
      isNew?: boolean,
    ) {
      const filteredPresets = getFilteredPresetsByFeeId(feedId, baseSourceId);

      return (
        filteredPresets.find((item) => item.id === presetId)?.name ||
        (isNew ? '' : CROSSBEAM_DEFAULT_LABEL)
      );
    }

    function getSourceFieldIdsByPresetId(
      feedId: number,
      baseSourceId?: number,
      id?: number,
    ) {
      const filteredPresets = getFilteredPresetsByFeeId(feedId, baseSourceId);
      const preset = filteredPresets.filter(
        (preset: DataSharePreset) => preset.id === id,
      );
      return flatMap(preset, (preset: DataSharePreset) =>
        preset.source_fields.map(({ source_field_id }) => source_field_id),
      );
    }

    function sortNamedOptions(options: PresetOptionsGroup[]) {
      const namedGroup = options[1];
      if (!namedGroup || !hasFeatureFlag(TEMP_TEAM_DEFAULT_PRESET)) return;
      namedGroup.items = sortBy(namedGroup.items, (item) =>
        item.label.toLowerCase(),
      );
    }

    function toPresetOption(
      preset: DataSharePreset,
      label: string,
    ): PresetOptionItem {
      return {
        value: preset.id,
        label,
        presetType: preset.preset_type ?? undefined,
        isDefault: isTeamDefault(preset),
      };
    }

    function getPresetOptionsPerFeed(
      presets: DataSharePreset[],
    ): Record<number, PresetOptionsGroup[]> {
      const options: Record<number, PresetOptionsGroup[]> = {};

      presets.forEach((preset: DataSharePreset) => {
        if (preset.preset_type === 'single_share_preset') return;

        // base_source_id is only filled if the preset is csv/gsheet type
        const parentId = preset.base_source_id ?? preset.feed_id;
        if (!options[parentId]) {
          options[parentId] = [
            {
              heading: null,
              items: [],
            },
          ];
        }

        const isCrossbeam = isCrossbeamPreset(preset);
        const groupIndex = isCrossbeam ? 0 : 1;

        if (!isCrossbeam && options[parentId].length === 1) {
          options[parentId].splice(1, 0, {
            heading: null,
            items: [],
          });
        }

        options[parentId]?.[groupIndex]?.items.push(
          toPresetOption(
            preset,
            getPresetName(
              preset.feed_id,
              preset.base_source_id || undefined,
              preset.id,
            ),
          ),
        );
      });

      Object.values(options).forEach(sortNamedOptions);

      return options;
    }

    function convertPresetsToOptions(
      presets: DataSharePreset[],
      feedId: number,
      sourceId?: number,
    ): PresetOptionsGroup[] {
      const options: PresetOptionsGroup[] = [
        {
          heading: null,
          items: [],
        },
      ];

      presets.forEach((preset) => {
        if (preset.preset_type === 'single_share_preset') return;

        const isCrossbeam = isCrossbeamPreset(preset);
        const groupIndex = isCrossbeam ? 0 : 1;

        if (!isCrossbeam && options.length === 1) {
          options.push({
            heading: null,
            items: [],
          });
        }

        options[groupIndex]?.items.push(
          toPresetOption(preset, getPresetName(feedId, sourceId, preset.id)),
        );
      });

      sortNamedOptions(options);

      return options;
    }

    /**
     * The option to pre-select for a new share. Once named presets sort A to Z
     * the team default is not necessarily first, so never pick by position.
     */
    function getDefaultOptionId(options: PresetOptionsGroup[]) {
      const items = options.flatMap((group) => group.items);
      const option = items.find((item) => item.isDefault) ?? items[0];
      return typeof option?.value === 'number' ? option.value : null;
    }

    refresh({ useCache: true });

    return {
      ready,
      readySync,
      allPresets,
      getPresetName,
      getDefaultPresetId,
      getCrossbeamPresetId,
      getDefaultOptionId,
      isTeamDefault,
      getFilteredPresetsByFeeId,
      getSourceFieldIdsByPresetId,
      getPresetOptionsPerFeed,
      convertPresetsToOptions,
      refreshDataSharePresetsStore: refresh,
    };
  },
);
